import { useState, useEffect } from 'react';
import api from '../services/api';

interface ShipmentReport {
  order_number: string;
  dealer_company: string;
  ship_to_city: string;
  ship_to_state: string;
  carrier_name: string;
  shipped_at: string;
  delivered_at: string | null;
  total_weight: number;
  freight_cost: number;
}

interface CarrierSummary {
  carrier_name: string;
  shipment_count: number;
  total_weight: number;
  total_cost: number;
  avg_delivery_days: number;
}

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState<'shipments' | 'carriers'>('shipments');
  const [shipments, setShipments] = useState<ShipmentReport[]>([]);
  const [carriers, setCarriers] = useState<CarrierSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadData();
  }, [activeTab, fromDate, toDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'shipments') {
        const data = await api.getShippingReport({ from_date: fromDate, to_date: toDate });
        setShipments(data);
      } else {
        const data = await api.getCarrierSummary(fromDate, toDate);
        setCarriers(data);
      }
    } catch (error) {
      console.error('Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Shipping Reports</h1>
          <p className="text-gray-400 mt-1">Analyze shipping performance and costs</p>
        </div>
      </div>

      {/* Date Filters */}
      <div className="bg-gray-800 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex-1" />
          <button
            onClick={loadData}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Generate Report
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('shipments')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'shipments'
                ? 'text-indigo-400 border-indigo-400'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            Shipment Details
          </button>
          <button
            onClick={() => setActiveTab('carriers')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'carriers'
                ? 'text-indigo-400 border-indigo-400'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            Carrier Summary
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      ) : activeTab === 'shipments' ? (
        /* Shipments Report */
        shipments.length === 0 ? (
          <div className="bg-gray-800 rounded-xl p-12 text-center">
            <p className="text-gray-400">No shipments found for the selected date range.</p>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Dealer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Destination</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Carrier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Shipped</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Delivered</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Weight</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {shipments.map((shipment, idx) => (
                    <tr key={idx} className="hover:bg-gray-750">
                      <td className="px-4 py-3 text-white font-medium">{shipment.order_number}</td>
                      <td className="px-4 py-3 text-gray-300">{shipment.dealer_company}</td>
                      <td className="px-4 py-3 text-gray-300">
                        {shipment.ship_to_city}, {shipment.ship_to_state}
                      </td>
                      <td className="px-4 py-3 text-gray-300">{shipment.carrier_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-300">{formatDate(shipment.shipped_at)}</td>
                      <td className="px-4 py-3 text-gray-300">{formatDate(shipment.delivered_at)}</td>
                      <td className="px-4 py-3 text-gray-300 text-right">{shipment.total_weight || 0} lbs</td>
                      <td className="px-4 py-3 text-gray-300 text-right">{formatCurrency(shipment.freight_cost || 0)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-900">
                  <tr>
                    <td colSpan={6} className="px-4 py-3 text-white font-medium">Total</td>
                    <td className="px-4 py-3 text-white font-medium text-right">
                      {shipments.reduce((sum, s) => sum + (s.total_weight || 0), 0)} lbs
                    </td>
                    <td className="px-4 py-3 text-white font-medium text-right">
                      {formatCurrency(shipments.reduce((sum, s) => sum + (s.freight_cost || 0), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )
      ) : (
        /* Carrier Summary */
        carriers.length === 0 ? (
          <div className="bg-gray-800 rounded-xl p-12 text-center">
            <p className="text-gray-400">No carrier data found for the selected date range.</p>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Carrier</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Shipments</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Total Weight</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Total Cost</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Avg Cost/Ship</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Avg Delivery Days</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {carriers.map((carrier, idx) => (
                    <tr key={idx} className="hover:bg-gray-750">
                      <td className="px-4 py-3 text-white font-medium">{carrier.carrier_name || 'Unknown'}</td>
                      <td className="px-4 py-3 text-gray-300 text-right">{carrier.shipment_count}</td>
                      <td className="px-4 py-3 text-gray-300 text-right">{carrier.total_weight || 0} lbs</td>
                      <td className="px-4 py-3 text-gray-300 text-right">{formatCurrency(carrier.total_cost || 0)}</td>
                      <td className="px-4 py-3 text-gray-300 text-right">
                        {formatCurrency((carrier.total_cost || 0) / (carrier.shipment_count || 1))}
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-right">
                        {carrier.avg_delivery_days ? `${carrier.avg_delivery_days.toFixed(1)} days` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-900">
                  <tr>
                    <td className="px-4 py-3 text-white font-medium">Total</td>
                    <td className="px-4 py-3 text-white font-medium text-right">
                      {carriers.reduce((sum, c) => sum + c.shipment_count, 0)}
                    </td>
                    <td className="px-4 py-3 text-white font-medium text-right">
                      {carriers.reduce((sum, c) => sum + (c.total_weight || 0), 0)} lbs
                    </td>
                    <td className="px-4 py-3 text-white font-medium text-right">
                      {formatCurrency(carriers.reduce((sum, c) => sum + (c.total_cost || 0), 0))}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )
      )}
    </div>
  );
}
